import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEO, getLocalBusinessSchema, getOrganizationSchema } from '@/components/seo/SEO';
import { 
  FloralCorner, 
  VineDivider, 
  FloralSeparator, 
  PaperLayerBg,
  FloatingPetals 
} from '@/components/ui/FloralElements';
import { 
  Heart, 
  Users, 
  ShoppingBag, 
  ArrowRight,
  Sparkles,
  TreePine,
  Leaf,
  ExternalLink,
  Facebook
} from 'lucide-react';

// Ranch photos from user uploads
const ranchPhotos = {
  calves: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/9exx0j39_IMG_2561.JPEG',
  horses: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/m973uo9g_IMG_2599.jpg',
  cowCalf: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/uc00byr0_IMG_3232.JPEG',
  herd: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/enbbzva9_IMG_4574.jpeg',
  goatKid: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/ybcfo7j1_IMG_6810.jpeg',
  // New photos
  womanGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/n5mw12s5_IMG_6869.JPG',
  childGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/1ejb7upe_IMG_7758.JPG',
  aurora: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/h2mpppe9_IMG_8669.jpeg',
  apron: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/xuuv78u0_0%203.21.09%E2%80%AFPM.jpg',
};

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/ob2xdoj4_279749100_449900870272747_652948079724847386_n.jpg';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Shop categories
const shopCategories = [
  {
    name: 'Goat Milk Soap',
    url: 'https://littlehouseonthetable.square.site/shop/goat-milk-soap/P4Y4YFP2KZWJFSPEXOI5AXLZ',
    icon: Sparkles
  },
  {
    name: 'Jams & Jellies',
    url: 'https://littlehouseonthetable.square.site/shop/jams-jellies-salsas-and-more/5XDB7H6GTEDMQUEYTAZKYYXG',
    icon: Leaf
  },
  {
    name: 'Aprons',
    url: 'https://littlehouseonthetable.square.site/shop/aprons/Z27PU6J2XCTJWWLFSMM3ATIZ',
    icon: Heart
  },
];

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-[90vh] flex items-center overflow-hidden">
    {/* Background */}
    <div className="absolute inset-0 bg-gradient-hero" />
    
    {/* Floating petals */}
    <FloatingPetals count={8} />
    
    {/* Decorative elements */}
    <div className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute bottom-20 right-10 w-48 h-48 bg-accent/5 rounded-full blur-3xl" />
    
    <div className="container mx-auto px-4 md:px-6 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center lg:text-left"
        >
          <motion.div variants={fadeInUp} className="mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Leaf className="h-4 w-4" />
              7th Generation Nebraska Ranch
            </span>
          </motion.div>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight mb-6"
          >
            Handmade with{' '}
            <span className="text-primary relative">
              Love
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-accent/50" viewBox="0 0 100 12" preserveAspectRatio="none">
                <path d="M0 8 Q 25 0 50 8 T 100 8" stroke="currentColor" strokeWidth="3" fill="none" />
              </svg>
            </span>
            <br />from Our Ranch
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8"
          >
            We are a working cattle ranch with a cow-calf operation and a handful of broodmares—committed 
            to sustainable agriculture, stewardship, and working with the land every day.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button 
              asChild 
              size="lg"
              className="btn-beveled px-8 py-6 text-base font-semibold"
            >
              <Link to="/shop" className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                Shop Our Products
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="px-8 py-6 text-base font-semibold border-2 border-primary/30 hover:bg-primary/5"
            >
              <Link to="/about" className="flex items-center gap-2">
                Our Story
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
        
        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <PaperLayerBg className="max-w-lg mx-auto">
            <div className="relative overflow-hidden rounded-xl">
              <img
                src={ranchPhotos.womanGoat}
                alt="Ranch life - holding a baby goat on our Nebraska homestead"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent" />
              
              {/* Floating badge */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="sticker-badge text-sm">
                  <Heart className="h-4 w-4 mr-1" />
                  Family Owned & Operated
                </div>
              </div>
            </div>
          </PaperLayerBg>
          
          {/* Floral decorations */}
          <FloralCorner position="top-right" className="opacity-40 -top-8 -right-8" />
        </motion.div>
      </div>
    </div>
  </section>
);

// Values Section
const ValuesSection = () => (
  <section className="py-20 bg-secondary relative">
    <FloralSeparator className="absolute top-0 left-0 right-0 -translate-y-1/2" />
    
    <div className="container mx-auto px-4 md:px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="grid md:grid-cols-3 gap-8"
      >
        {[
          {
            icon: Heart,
            title: 'Ethically Raised',
            description: 'Our goats have names and personalities. We treat them as family because they are.'
          },
          {
            icon: Leaf,
            title: 'All Natural',
            description: 'No chemicals or shortcuts. Just natural products that are good for you and the earth.'
          },
          {
            icon: Users,
            title: '7th Generation',
            description: 'Our kids are learning the family business on the same homestead our ancestors settled.'
          }
        ].map((value, index) => (
          <motion.div key={index} variants={fadeInUp}>
            <Card className="raised-card h-full text-center p-8 border-0">
              <CardContent className="p-0">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <value.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-muted-foreground">
                  {value.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

// Shop Categories Section
const ShopCategoriesSection = () => (
  <section className="py-20 relative overflow-hidden">
    <div className="container mx-auto px-4 md:px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="text-center mb-12"
      >
        <motion.span 
          variants={fadeInUp}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4"
        >
          <ShoppingBag className="h-4 w-4" />
          Shop Our Products
        </motion.span>
        <motion.h2 
          variants={fadeInUp}
          className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4"
        >
          Browse Our Categories
        </motion.h2>
        <motion.p 
          variants={fadeInUp}
          className="text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          Handmade, homegrown, and homemade — every product crafted with care on our Nebraska family ranch.
        </motion.p>
      </motion.div>
      
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={staggerContainer}
        className="grid sm:grid-cols-3 gap-6 md:gap-8 max-w-3xl mx-auto"
      >
        {shopCategories.map((category, index) => (
          <motion.div key={index} variants={fadeInUp}>
            <Link
              to="/shop"
              className="block group"
            >
              <Card className="raised-card overflow-hidden border-0 text-center p-6">
                <CardContent className="p-0">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <category.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {category.name}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-sm text-accent font-medium">
                    Shop Now <ArrowRight className="h-3 w-3" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="text-center mt-10"
      >
        <Button 
          asChild 
          size="lg"
          className="btn-beveled-sage px-8 py-6 text-base font-semibold"
        >
          <Link
            to="/shop"
            className="flex items-center gap-2"
          >
            Browse Full Store
            <ArrowRight className="h-5 w-5" />
          </Link>
        </Button>
      </motion.div>
    </div>
  </section>
);

// Ranch Story Teaser
const RanchStorySection = () => (
  <section className="py-20 bg-gradient-floral relative overflow-hidden">
    <FloralCorner position="top-left" className="opacity-30" />
    <FloralCorner position="bottom-right" className="opacity-30" />
    
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <PaperLayerBg>
            <div className="grid grid-cols-2 gap-4 p-4">
              <img
                src={ranchPhotos.childGoat}
                alt="Child bottle feeding a baby goat on our ranch"
                className="rounded-lg h-48 md:h-64 object-cover w-full"
                loading="lazy"
                width="400"
                height="256"
              />
              <img
                src={ranchPhotos.horses}
                alt="Family horseback riding on the ranch"
                className="rounded-lg h-48 md:h-64 object-cover w-full"
                loading="lazy"
                width="400"
                height="256"
              />
              <img
                src={ranchPhotos.aurora}
                alt="Beautiful Nebraska sky over our barn"
                className="rounded-lg h-48 md:h-64 object-cover w-full col-span-2"
                loading="lazy"
                width="800"
                height="256"
              />
            </div>
          </PaperLayerBg>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <TreePine className="h-4 w-4" />
            Our Story
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-6">
            Rooted in Real, Working Land
          </h2>
          <p className="text-muted-foreground mb-4 leading-relaxed">
            Little House on the Table is rooted in real, working land. We are a working cattle ranch 
            with a cow-calf operation and a handful of broodmares, and our days revolve around the 
            rhythms of the seasons.
          </p>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            Our mission is simple: practice sustainable agriculture and stewardship. We believe in 
            working with the land—protecting soil health, respecting pasture cycles, and making 
            thoughtful choices that support long-term resilience.
          </p>
          <Button 
            asChild 
            variant="outline"
            size="lg"
            className="border-2 border-primary/30 hover:bg-primary/5"
          >
            <Link to="/about" className="flex items-center gap-2">
              Read Our Full Story
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </div>
  </section>
);

// Reviews Section - Links to Facebook
const ReviewsSection = () => (
  <section className="py-20 relative">
    <VineDivider className="absolute top-0 left-0 right-0" />
    
    <div className="container mx-auto px-4 md:px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="text-center mb-12"
      >
        <motion.h2 
          variants={fadeInUp}
          className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
        >
          What Our Customers Say
        </motion.h2>
        <motion.p 
          variants={fadeInUp}
          className="text-lg text-muted-foreground"
        >
          See what our customers are saying on Facebook
        </motion.p>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mx-auto text-center"
      >
        <Card className="raised-card border-0 p-8">
          <CardContent className="p-0">
            <Facebook className="h-16 w-16 mx-auto mb-4 text-primary/60" />
            <h3 className="font-serif text-2xl font-semibold text-foreground mb-4">
              Read Our Facebook Reviews
            </h3>
            <p className="text-muted-foreground mb-6">
              Our customers love sharing their experiences with Little House on the Table products. 
              Visit our Facebook page to see real reviews from real people!
            </p>
            <Button 
              asChild 
              size="lg"
              className="btn-beveled px-8 py-6"
            >
              <a
                href="https://www.facebook.com/LittleHouseontheTable/reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Facebook className="h-5 w-5" />
                See Reviews on Facebook
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  </section>
);

// Newsletter Section
const NewsletterSection = () => (
  <section className="py-20 bg-primary relative overflow-hidden">
    {/* Decorative elements */}
    <div className="absolute inset-0 opacity-10">
      <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/2 -translate-y-1/2" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="2" fill="none" />
        <circle cx="100" cy="100" r="60" stroke="white" strokeWidth="1" fill="none" />
      </svg>
      <svg className="absolute bottom-0 right-0 w-96 h-96 translate-x-1/3 translate-y-1/3" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="2" fill="none" />
        <circle cx="100" cy="100" r="40" stroke="white" strokeWidth="1" fill="none" />
      </svg>
    </div>
    
    <div className="container mx-auto px-4 md:px-6 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mx-auto text-center"
      >
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
          Join Our Ranch Family
        </h2>
        <p className="text-primary-foreground/80 mb-8">
          Follow us on Facebook for updates on new products, ranch life stories, and special offers.
        </p>
        <Button 
          asChild
          size="lg"
          className="bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-6 font-semibold"
        >
          <a
            href="https://www.facebook.com/LittleHouseontheTable"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <Facebook className="h-5 w-5" />
            Follow Us on Facebook
          </a>
        </Button>
      </motion.div>
    </div>
  </section>
);

// Main Home Component
export const HomePage = () => {
  // Combine LocalBusiness and Organization schemas
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getLocalBusinessSchema(),
      getOrganizationSchema()
    ]
  };

  return (
    <>
      <SEO
        title={null}
        description="Little House on the Table - A 7th generation Nebraska family ranch in Crawford, NE. Shop handmade goat milk soaps, homemade jams, jellies, salsas, and handcrafted aprons. Sustainable agriculture and products made with love."
        path="/"
        structuredData={combinedSchema}
        keywords="goat milk soap, Nebraska ranch, handmade soap, Crawford Nebraska, jams, jellies, salsas, aprons, sustainable agriculture, family farm, 7th generation ranch, natural soap, homemade products"
      />
      <HeroSection />
      <ValuesSection />
      <ShopCategoriesSection />
      <RanchStorySection />
      <ReviewsSection />
      <NewsletterSection />
    </>
  );
};

export default HomePage;
